import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {BANNER_HEIGHT, BANNER_CONTENT_OFFSET} from './positioning';

const BannerStackContext = createContext(null);

export const BannerStackProvider = ({children}) => {
  const [banners, setBanners] = useState({});

  useEffect(() => {
    const entries = Object.values(banners);
    document.body.style.setProperty(
      '--banner-stack-total',
      String(entries.length)
    );
    const overlap = BANNER_HEIGHT - BANNER_CONTENT_OFFSET;
    const stackHeight = entries.reduce((sum, {height}) => sum + height, 0);
    const totalContentOffset = stackHeight
      ? Math.max(0, stackHeight - overlap)
      : 0;
    document.body.style.setProperty(
      '--banner-stack-content-offset',
      `${totalContentOffset}px`
    );
  }, [banners]);

  useEffect(() => {
    const header = document.querySelector('header');
    if (!header) return undefined;

    let current = null;

    const measure = () => {
      const rside = header.querySelector('.HeaderRside');
      const {top, bottom} = header.getBoundingClientRect();
      const extent = Math.floor(
        Math.max(bottom, rside ? rside.getBoundingClientRect().bottom : 0) - top
      );
      if (extent === current) return;
      current = extent;
      document.body.style.setProperty('--banner-stack-top', `${extent}px`);
    };

    measure();
    window.addEventListener('resize', measure);

    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(header);
    const rside = header.querySelector('.HeaderRside');
    if (rside) observer.observe(rside);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Stable identities so consumers' effects don't re-fire on every banners
  // update — otherwise register/unregister cascade infinitely.
  const register = useCallback((key, priority, height) => {
    setBanners((prev) => {
      const entry = prev[key];
      if (entry && entry.priority === priority && entry.height === height) {
        return prev;
      }
      return {...prev, [key]: {priority, height}};
    });
  }, []);

  const unregister = useCallback((key) => {
    setBanners((prev) => {
      if (!(key in prev)) return prev;
      const next = {...prev};
      delete next[key];
      return next;
    });
  }, []);

  const value = useMemo(() => ({banners, register, unregister}), [
    banners,
    register,
    unregister
  ]);

  return (
    <BannerStackContext.Provider value={value}>
      {children}
    </BannerStackContext.Provider>
  );
};

BannerStackProvider.propTypes = {
  children: PropTypes.node.isRequired
};

// Pass `active=false` for components that mount unconditionally but only
// render visibly some of the time (internal `if (...) return null;`).
export const useBannerSlot = (
  key,
  priority,
  active = true,
  height = BANNER_HEIGHT
) => {
  const ctx = useContext(BannerStackContext);
  const register = ctx?.register;
  const unregister = ctx?.unregister;
  const banners = ctx?.banners;

  useEffect(() => {
    if (!register || !active) return undefined;
    register(key, priority, height);
    return () => unregister(key);
  }, [register, unregister, key, priority, height, active]);

  return useMemo(() => {
    if (!banners) return 0;
    return Object.entries(banners).filter(
      ([otherKey, otherEntry]) =>
        otherKey !== key && otherEntry.priority < priority
    ).length;
  }, [banners, key, priority]);
};
